/**
 * Application logger with environment-aware log suppression.
 *
 * Active levels per environment:
 * - development: info, success, warning, error
 * - production:  warning, error only
 *
 * Usage:
 *   Logger.info({ message: 'Config loaded', content: projectId })
 *   Logger.warning({ message: 'Missing field', content: 'price' })
 *   Logger.error({ message: 'Fetch failed', content: url })
 */
class Logger {
  static #style = 'font-weight: bold; padding: 2px 6px; border-radius: 3px;'

  /**
   * @param {{ message: string, content?: string }} params
   * @param {string} color - CSS color value for the log prefix badge
   */
  static #log({ message, content }, color) {
    const prefixStyle = `background: black; color: ${color}; ${Logger.#style}`

    if (content) {
      console.info(
        '%c[BIM-CADAPLUS]%c ' + message + '; %c' + content,
        prefixStyle,
        '',
        'font-style: italic;'
      )
    } else {
      console.info(
        '%c[BIM-CADAPLUS]%c ' + message,
        prefixStyle,
        ''
      )
    }
  }

  /**
   * Informational log. Active in development builds only.
   * @param {{ message: string, content?: string }} params
   */
  static debug(params) {
    if (import.meta.env.DEV) Logger.#log(params, '#a2a2a2')
  }

  /**
   * Informational log. Active in development builds only.
   * @param {{ message: string, content?: string }} params
   */
  static info(params) {
    if (import.meta.env.DEV) Logger.#log(params, '#3b82f6')
  }

  /**
   * Success log. Active in development builds only.
   * @param {{ message: string, content?: string }} params
   */
  static success(params) {
    if (import.meta.env.DEV) Logger.#log(params, '#22c55e')
  }

  /**
   * Warning log. Active in all environments.
   * Use for recoverable issues that should be visible in production.
   * @param {{ message: string, content?: string }} params
   */
  static warning(params) {
    Logger.#log(params, '#f97316')
  }

  /**
   * Error log. Active in all environments.
   * Use for failures that affect application behavior.
   * @param {{ message: string, content?: string }} params
   */
  static error(params) {
    Logger.#log(params, '#ef4444')
  }
}

export { Logger }
